import { usePermission } from "src/helpers/Auth";
import { useGetDataset } from "../services/DatasetServices";
import { Dataset } from "../types";

/**
 * Whether the current user may delete records from `datasetId`. Mirrors the
 * backend's two-axis check on DELETE /datasets/delete-sdr-by-id: the role
 * must hold the `dataset:delete` capability AND the user must hold WRITE on
 * this specific dataset (record deletion is a content mutation, so it is
 * write-gated per-object — a view-only grantee with the role capability is
 * denied). Resolves via the dataset detail already in the react-query cache.
 *
 * Lives in the viewer layer (not DatasetServices) on purpose: it pulls in the
 * auth helpers, whose import graph reaches UI modules — the data-service
 * module must stay free of that.
 *
 * Strict: returns false while the dataset (and its my_access) is loading.
 */
export const useCanDeleteDatasetRecords = (
  datasetId: number | undefined,
): boolean => {
  const { canIt: hasDeleteCapability } = usePermission("dataset:delete");
  const { data: dataset } = useGetDataset(datasetId) as {
    data: Dataset | undefined;
  };
  return Boolean(hasDeleteCapability) && dataset?.my_access?.can_write === true;
};
